import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { AlertTriangle, Copy, Edit, ExternalLink, Gift, Image, Loader2, MapPin, Plus, Search, Trash2, Upload, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { trpc } from '~/api/trpc';
import DiscountCodeAssignmentList from '../discount-codes/DiscountCodeAssignmentList';
import { TagPreview } from './TagsEditor';

type BookableItemType = 'SERVICE' | 'BUNDLE';

type BookableItemFormData = {
    nameDe: string;
    nameEn: string;
    descriptionDe: string;
    descriptionEn: string;
    keyBenefitsDe: string[];
    keyBenefitsEn: string[];
    youShouldKnowThisDe: string;
    youShouldKnowThisEn: string;
    hideFromStaff: boolean;
    hideFromPublic: boolean;
    isDigital: boolean;
    launchDate: string | null;
};

const emptyFormData: BookableItemFormData = {
    nameDe: '',
    nameEn: '',
    descriptionDe: '',
    descriptionEn: '',
    keyBenefitsDe: [],
    keyBenefitsEn: [],
    youShouldKnowThisDe: '',
    youShouldKnowThisEn: '',
    hideFromStaff: false,
    hideFromPublic: false,
    isDigital: false,
    launchDate: null,
};

const BookableItemsEditor = () => {
    const navigate = useNavigate();
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [editingItemId, setEditingItemId] = useState<string | null>(null);
    const [formData, setFormData] = useState<BookableItemFormData>(emptyFormData);
    const [newBenefitDe, setNewBenefitDe] = useState('');
    const [newBenefitEn, setNewBenefitEn] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const [variantSearch, setVariantSearch] = useState('');
    const [addOnVariantSearch, setAddOnVariantSearch] = useState('');
    const [newAddOn, setNewAddOn] = useState({ nameDe: '', nameEn: '', descriptionDe: '', descriptionEn: '', variantId: '' });
    const [newVariantOptionLabel, setNewVariantOptionLabel] = useState({ de: '', en: '' });
    const [localStoreIds, setLocalStoreIds] = useState<number[]>([]);
    const [localTagIds, setLocalTagIds] = useState<string[]>([]);
    const [localDiscountCodeIds, setLocalDiscountCodeIds] = useState<string[]>([]);
    // Type selection state for create dialog
    const [createType, setCreateType] = useState<BookableItemType>('SERVICE');
    const [createBundleId, setCreateBundleId] = useState<string>('');
    const [bundleSearch, setBundleSearch] = useState('');
    // Filter state: null = all, 'unassigned' = no category, 'cat-{id}' = category, 'sym-{id}' = symptom category
    const [selectedFilter, setSelectedFilter] = useState<string | null>(null);
    const [nameSearch, setNameSearch] = useState('');
    const fileInputRef = useRef<HTMLInputElement>(null);

    const utils = trpc.useUtils();
    const { data: itemsData, isLoading } = trpc.bookingFunnelCMS.getBookableItems.useQuery();
    const { data: categoriesData } = trpc.bookingFunnelCMS.getCategories.useQuery();
    const { data: symptomCategoriesData } = trpc.bookingFunnelCMS.getSymptomCategories.useQuery();
    const { data: variantsData } = trpc.bookingFunnelCMS.getTreatmentVariantsForSelection.useQuery(
        { search: variantSearch },
        { enabled: variantSearch.length > 0 || editingItemId !== null }
    );
    
    const { data: storesData } = trpc.bookingFunnelCMS.getStoresForSelection.useQuery();
    const { data: bundlesData } = trpc.bookingFunnelCMS.getBundles.useQuery();
    const { data: tagsData } = trpc.bookingFunnelCMS.getTags.useQuery();
    const { data: discountCodesData } = trpc.discountCodes.getForBookableItem.useQuery(
        { bookableItemId: editingItemId ?? '' },
        { enabled: editingItemId !== null }
    );

    const editingItem = itemsData?.data?.find((item) => item.id === editingItemId);
    const discountCodes = discountCodesData?.data || [];
    const assignedDiscountCodeIds = discountCodes.filter((code) => code.isAssigned).map((code) => code.id);
    const hasDiscountCodeChanges = discountCodesData !== undefined && (
        localDiscountCodeIds.length !== assignedDiscountCodeIds.length ||
        localDiscountCodeIds.some((id) => !assignedDiscountCodeIds.includes(id))
    );
    const availableBundles = bundlesData?.data || [];
    const stores = storesData?.data || [];
    const tags = tagsData?.data || [];
    const editingItemStoreIds = editingItem?.storeAssociations?.map((sa) => sa.store.id) || [];

    const createMutation = trpc.bookingFunnelCMS.createBookableItem.useMutation({
        onSuccess: (result) => {
            toast.success('Buchbare Leistung erstellt');
            utils.bookingFunnelCMS.getBookableItems.invalidate();
            setIsCreateDialogOpen(false);
            setFormData(emptyFormData);
            setCreateType('SERVICE');
            setCreateBundleId('');
            setBundleSearch('');
            // Open edit dialog for the new item
            setEditingItemId(result.data.id);
        },
        onError: (error) => {
            toast.error('Fehler beim Erstellen', { description: error.message });
        },
    });

    const updateMutation = trpc.bookingFunnelCMS.updateBookableItem.useMutation({
        onSuccess: () => {
            toast.success('Änderungen gespeichert');
            utils.bookingFunnelCMS.getBookableItems.invalidate();
        },
        onError: (error) => {
            toast.error('Fehler beim Speichern', { description: error.message });
        },
    });

    const deleteMutation = trpc.bookingFunnelCMS.deleteBookableItem.useMutation({
        onSuccess: () => {
            toast.success('Buchbare Leistung gelöscht');
            utils.bookingFunnelCMS.getBookableItems.invalidate();
            setEditingItemId(null);
        },
        onError: (error) => {
            toast.error('Fehler beim Löschen', { description: error.message });
        },
    });

    // Image mutations
    const uploadImageMutation = trpc.bookingFunnelCMS.uploadBookableItemImage.useMutation({
        onSuccess: () => {
            toast.success('Bild hochgeladen');
            utils.bookingFunnelCMS.getBookableItems.invalidate();
            setIsUploading(false);
        },
        onError: (error) => {
            toast.error('Fehler beim Hochladen', { description: error.message });
            setIsUploading(false);
        },
    });

    const deleteImageMutation = trpc.bookingFunnelCMS.deleteBookableItemImage.useMutation({
        onSuccess: () => {
            toast.success('Bild gelöscht');
            utils.bookingFunnelCMS.getBookableItems.invalidate();
        },
        onError: (error) => {
            toast.error('Fehler beim Löschen', { description: error.message });
        },
    });

    // Variant option mutations
    const addVariantOptionMutation = trpc.bookingFunnelCMS.addVariantOption.useMutation({
        onSuccess: () => {
            toast.success('Variante hinzugefügt');
            utils.bookingFunnelCMS.getBookableItems.invalidate();
            setNewVariantOptionLabel({ de: '', en: '' });
        },
        onError: (error) => {
            toast.error('Fehler', { description: error.message });
        },
    });

    const updateVariantOptionMutation = trpc.bookingFunnelCMS.updateVariantOption.useMutation({
        onSuccess: () => {
            toast.success('Variante aktualisiert');
            utils.bookingFunnelCMS.getBookableItems.invalidate();
        },
        onError: (error) => {
            toast.error('Fehler', { description: error.message });
        },
    });

    const removeVariantOptionMutation = trpc.bookingFunnelCMS.removeVariantOption.useMutation({
        onSuccess: () => {
            toast.success('Variante entfernt');
            utils.bookingFunnelCMS.getBookableItems.invalidate();
        },
        onError: (error) => {
            toast.error('Fehler', { description: error.message });
        },
    });

    // Add-on mutations
    const addAddOnMutation = trpc.bookingFunnelCMS.addAddOn.useMutation({
        onSuccess: () => {
            toast.success('Add-On hinzugefügt');
            utils.bookingFunnelCMS.getBookableItems.invalidate();
            setNewAddOn({ nameDe: '', nameEn: '', descriptionDe: '', descriptionEn: '', variantId: '' });
        },
        onError: (error) => {
            toast.error('Fehler', { description: error.message });
        },
    });

    const updateAddOnMutation = trpc.bookingFunnelCMS.updateAddOn.useMutation({
        onSuccess: () => {
            toast.success('Add-On aktualisiert');
            utils.bookingFunnelCMS.getBookableItems.invalidate();
        },
        onError: (error) => {
            toast.error('Fehler', { description: error.message });
        },
    });

    const removeAddOnMutation = trpc.bookingFunnelCMS.removeAddOn.useMutation({
        onSuccess: () => {
            toast.success('Add-On entfernt');
            utils.bookingFunnelCMS.getBookableItems.invalidate();
        },
        onError: (error) => {
            toast.error('Fehler', { description: error.message });
        },
    });

    // Duplicate mutation
    const duplicateMutation = trpc.bookingFunnelCMS.duplicateBookableItem.useMutation({
        onSuccess: (result) => {
            toast.success('Leistung kopiert');
            utils.bookingFunnelCMS.getBookableItems.invalidate();
            // Open the edit dialog for the new item
            setEditingItemId(result.data.id);
        },
        onError: (error) => {
            toast.error('Fehler beim Kopieren', { description: error.message });
        },
    });

    // Store mutation
    const setStoresMutation = trpc.bookingFunnelCMS.setBookableItemStores.useMutation({
        onSuccess: () => {
            toast.success('Standorte aktualisiert');
            utils.bookingFunnelCMS.getBookableItems.invalidate();
        },
        onError: (error) => {
            toast.error('Fehler', { description: error.message });
        },
    });

    // Tag mutation
    const setTagsMutation = trpc.bookingFunnelCMS.setBookableItemTags.useMutation({
        onSuccess: () => {
            utils.bookingFunnelCMS.getBookableItems.invalidate();
            utils.bookingFunnelCMS.getTags.invalidate();
        },
        onError: (error) => {
            toast.error('Fehler beim Speichern der Tags', { description: error.message });
        },
    });

    const setDiscountCodesMutation = trpc.discountCodes.setForBookableItem.useMutation({
        onSuccess: () => {
            toast.success('Rabattcodes aktualisiert');
            utils.discountCodes.getForBookableItem.invalidate();
            utils.discountCodes.getOne.invalidate();
            utils.discountCodes.getAll.invalidate();
        },
        onError: (error) => {
            toast.error('Fehler beim Speichern der Rabattcodes', { description: error.message });
        },
    });

    // Must run before the discountCodesData effect so cached data for the next item isn't wiped.
    useEffect(() => {
        setLocalDiscountCodeIds([]);
    }, [editingItemId]);

    useEffect(() => {
        if (discountCodesData?.data) {
            setLocalDiscountCodeIds(discountCodesData.data.filter((code) => code.isAssigned).map((code) => code.id));
        }
    }, [discountCodesData]);

    // Load editing item data into form
    useEffect(() => {
        if (editingItem) {
            setFormData({
                nameDe: editingItem.nameDe,
                nameEn: editingItem.nameEn,
                descriptionDe: editingItem.descriptionDe,
                descriptionEn: editingItem.descriptionEn,
                keyBenefitsDe: editingItem.keyBenefitsDe || [],
                keyBenefitsEn: editingItem.keyBenefitsEn || [],
                youShouldKnowThisDe: editingItem.youShouldKnowThisDe,
                youShouldKnowThisEn: editingItem.youShouldKnowThisEn,
                hideFromStaff: editingItem.hideFromStaff,
                hideFromPublic: editingItem.hideFromPublic,
                isDigital: editingItem.isDigital,
                launchDate: editingItem.launchDate ? new Date(editingItem.launchDate).toISOString().split('T')[0] : null,
            });
            // Initialize local store IDs
            setLocalStoreIds(editingItem.storeAssociations?.map((sa) => sa.store.id) || []);
            setLocalTagIds(editingItem.tagAssociations?.map((ta) => ta.tag.id) || []);
        }
    }, [editingItem]);

    const handleCreate = () => {
        if (!formData.nameDe || !formData.nameEn) {
            toast.error('Bitte geben Sie einen Namen ein');
            return;
        }
        if (createType === 'BUNDLE' && !createBundleId) {
            toast.error('Bitte wählen Sie ein Bundle aus');
            return;
        }
        createMutation.mutate({
            nameDe: formData.nameDe,
            nameEn: formData.nameEn,
            descriptionDe: formData.descriptionDe,
            descriptionEn: formData.descriptionEn,
            type: createType,
            bundleId: createType === 'BUNDLE' ? createBundleId : undefined,
        });
    };

    const handleSave = () => {
        if (!editingItemId) return;
        
        // Save main form data
        updateMutation.mutate({
            id: editingItemId,
            ...formData,
        });

        // Save store associations if they've changed
        const currentStoreIds = editingItem?.storeAssociations?.map((sa) => sa.store.id) || [];
        if (JSON.stringify(localStoreIds.sort()) !== JSON.stringify(currentStoreIds.sort())) {
            setStoresMutation.mutate({
                bookableItemId: editingItemId,
                storeIds: localStoreIds,
            });
        }

        // Save tag associations if they've changed
        const currentTagIds = editingItem?.tagAssociations?.map((ta) => ta.tag.id) || [];
        if (JSON.stringify(localTagIds) !== JSON.stringify(currentTagIds)) {
            setTagsMutation.mutate({
                bookableItemId: editingItemId,
                tagIds: localTagIds,
            });
        }

        if (hasDiscountCodeChanges) {
            setDiscountCodesMutation.mutate({
                bookableItemId: editingItemId,
                discountCodeIds: localDiscountCodeIds,
            });
        }
    };

    const handleToggleTag = (tagId: string) => {
        setLocalTagIds((current) =>
            current.includes(tagId) ? current.filter((id) => id !== tagId) : [...current, tagId]
        );
    };

    const handleDelete = (id: string) => {
        if (confirm('Möchten Sie diese buchbare Leistung wirklich löschen?')) {
            deleteMutation.mutate({ id });
        }
    };

    const handleImageUpload = async (file: File) => {
        if (!editingItemId) return;

        if (file.size > 10 * 1024 * 1024) {
            toast.error('Bild ist zu groß (max. 10MB)');
            return;
        }

        setIsUploading(true);
        const reader = new FileReader();
        reader.onload = () => {
            const base64 = reader.result as string;
            uploadImageMutation.mutate({
                bookableItemId: editingItemId,
                imageBase64: base64,
            });
        };
        reader.readAsDataURL(file);
    };

    const handleAddBenefit = (lang: 'de' | 'en') => {
        const benefit = lang === 'de' ? newBenefitDe : newBenefitEn;
        if (!benefit.trim()) return;

        const key = lang === 'de' ? 'keyBenefitsDe' : 'keyBenefitsEn';
        setFormData((prev) => ({
            ...prev,
            [key]: [...prev[key], benefit.trim()],
        }));

        if (lang === 'de') {
            setNewBenefitDe('');
        } else {
            setNewBenefitEn('');
        }
    };

    const handleRemoveBenefit = (lang: 'de' | 'en', index: number) => {
        const key = lang === 'de' ? 'keyBenefitsDe' : 'keyBenefitsEn';
        setFormData((prev) => ({
            ...prev,
            [key]: prev[key].filter((_, i) => i !== index),
        }));
    };

    const handleAddVariantOption = (variantId: string) => {
        if (!editingItemId) return;
        addVariantOptionMutation.mutate({
            bookableItemId: editingItemId,
            treatmentVariantId: variantId,
            optionLabelDe: newVariantOptionLabel.de,
            optionLabelEn: newVariantOptionLabel.en,
        });
    };

    const handleAddAddOn = () => {
        if (!editingItemId || !newAddOn.variantId || !newAddOn.nameDe || !newAddOn.nameEn) {
            toast.error('Bitte füllen Sie alle Pflichtfelder aus');
            return;
        }
        addAddOnMutation.mutate({
            bookableItemId: editingItemId,
            treatmentVariantId: newAddOn.variantId,
            nameDe: newAddOn.nameDe,
            nameEn: newAddOn.nameEn,
            descriptionDe: newAddOn.descriptionDe,
            descriptionEn: newAddOn.descriptionEn,
        });
    };

    const allItems = itemsData?.data || [];
    const categories = categoriesData?.data || [];
    const symptomCategories = symptomCategoriesData?.data || [];
    const availableVariants = variantsData?.data || [];

    // Filter items based on selected filter and name search
    const items = (() => {
        let filtered = allItems;
        
        // Apply category/symptom filter
        if (selectedFilter === 'unassigned') {
            filtered = filtered.filter((item) => 
                (!item.categoryAssociations || item.categoryAssociations.length === 0) &&
                (!item.symptomCategoryAssociations || item.symptomCategoryAssociations.length === 0)
            );
        } else if (selectedFilter?.startsWith('cat-')) {
            const categoryId = selectedFilter.replace('cat-', '');
            filtered = filtered.filter((item) =>
                item.categoryAssociations?.some((ca) => ca.category.id === categoryId)
            );
        } else if (selectedFilter?.startsWith('sym-')) {
            const symptomCategoryId = selectedFilter.replace('sym-', '');
            filtered = filtered.filter((item) =>
                item.symptomCategoryAssociations?.some((sca) => sca.symptomCategory.id === symptomCategoryId)
            );
        }
        
        // Apply name search filter
        if (nameSearch.trim()) {
            const searchLower = nameSearch.toLowerCase().trim();
            filtered = filtered.filter((item) =>
                item.nameDe.toLowerCase().includes(searchLower) ||
                item.nameEn.toLowerCase().includes(searchLower)
            );
        }
        
        return filtered;
    })();

    // Count items per category for the navigation
    const categoryCounts = categories.reduce((acc, cat) => {
        acc[cat.id] = allItems.filter((item) =>
            item.categoryAssociations?.some((ca) => ca.category.id === cat.id)
        ).length;
        return acc;
    }, {} as Record<string, number>);

    // Count items per symptom category for the navigation
    const symptomCategoryCounts = symptomCategories.reduce((acc, cat) => {
        acc[cat.id] = allItems.filter((item) =>
            item.symptomCategoryAssociations?.some((sca) => sca.symptomCategory.id === cat.id)
        ).length;
        return acc;
    }, {} as Record<string, number>);

    const unassignedCount = allItems.filter(
        (item) => 
            (!item.categoryAssociations || item.categoryAssociations.length === 0) &&
            (!item.symptomCategoryAssociations || item.symptomCategoryAssociations.length === 0)
    ).length;

    // Helper function to check store mismatches for an item
    const getItemStoreMismatchInfo = (item: typeof items[0]) => {
        const itemStoreIds = item.storeAssociations?.map((sa) => sa.store.id) || [];
        const itemHasNoRestrictions = itemStoreIds.length === 0;

        let hasVariantMismatch = false;
        let hasAddOnMismatch = false;
        const variantIssues: { name: string; itemStores: string[]; variantStores: string[]; overlapping: string[] }[] = [];
        const addOnIssues: { name: string; itemStores: string[]; variantStores: string[]; overlapping: string[] }[] = [];

        // Check variants
        item.variantOptions?.forEach((vo) => {
            const variantStoreAssociations = (vo.treatmentVariant as { storeAssociations?: { store: { id: number; name: string } }[] }).storeAssociations || [];
            const variantStoreIds = variantStoreAssociations.map((sa) => sa.store.id);
            const variantHasNoRestrictions = variantStoreIds.length === 0;

            if (!variantHasNoRestrictions && !itemHasNoRestrictions) {
                const overlapping = itemStoreIds.filter((id) => variantStoreIds.includes(id));
                if (overlapping.length === 0) {
                    hasVariantMismatch = true;
                    variantIssues.push({
                        name: vo.treatmentVariant.name,
                        itemStores: item.storeAssociations?.map((sa) => sa.store.name) || [],
                        variantStores: variantStoreAssociations.map((sa) => sa.store.name),
                        overlapping: [],
                    });
                }
            } else if (!variantHasNoRestrictions && itemHasNoRestrictions) {
                // Item is everywhere, variant is limited - this is fine but worth noting
            } else if (variantHasNoRestrictions && !itemHasNoRestrictions) {
                // Variant is everywhere, item is limited - this is fine
            }
        });

        // Check add-ons
        item.addOns?.forEach((addOn) => {
            const variantStoreAssociations = (addOn.treatmentVariant as { storeAssociations?: { store: { id: number; name: string } }[] }).storeAssociations || [];
            const variantStoreIds = variantStoreAssociations.map((sa) => sa.store.id);
            const variantHasNoRestrictions = variantStoreIds.length === 0;

            if (!variantHasNoRestrictions && !itemHasNoRestrictions) {
                const overlapping = itemStoreIds.filter((id) => variantStoreIds.includes(id));
                if (overlapping.length === 0) {
                    hasAddOnMismatch = true;
                    addOnIssues.push({
                        name: addOn.nameDe,
                        itemStores: item.storeAssociations?.map((sa) => sa.store.name) || [],
                        variantStores: variantStoreAssociations.map((sa) => sa.store.name),
                        overlapping: [],
                    });
                }
            }
        });

        return {
            hasAnyMismatch: hasVariantMismatch || hasAddOnMismatch,
            hasVariantMismatch,
            hasAddOnMismatch,
            variantIssues,
            addOnIssues,
        };
    };

    // Get mismatch info for the currently editing item
    const editingItemMismatchInfo = editingItem ? getItemStoreMismatchInfo(editingItem) : null;

    if (isLoading) {
        return (
            <div className='space-y-4'>
                <Skeleton className='h-10 w-48' />
                <Skeleton className='h-64 w-full' />
            </div>
        );
    }

    return (
        <div className='space-y-6'>
            <div className='flex justify-between items-center'>
                <p className='text-sm text-muted-foreground'>
                    Buchbare Leistungen sind die Elemente, die Kunden im Buchungs-Funnel sehen und auswählen können.
                </p>
                <div className='flex gap-2'>
                    <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                        <DialogTrigger asChild>
                            <Button>
                                <Plus className='mr-2 h-4 w-4' />
                                Neue Leistung
                            </Button>
                        </DialogTrigger>
                    <DialogContent className='max-w-2xl'>
                        <DialogHeader>
                            <DialogTitle>Neue buchbare Leistung erstellen</DialogTitle>
                            <DialogDescription>
                                Erstellen Sie eine neue buchbare Leistung. Nach dem Erstellen können Sie Details bearbeiten.
                            </DialogDescription>
                        </DialogHeader>
                        <div className='space-y-4 py-4'>
                            {/* Type Selection */}
                            <div className='space-y-2'>
                                <Label>Typ *</Label>
                                <Select value={createType} onValueChange={(v) => {
                                    setCreateType(v as BookableItemType);
                                    if (v === 'SERVICE') {
                                        setCreateBundleId('');
                                        setBundleSearch('');
                                    }
                                }}>
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value='SERVICE'>Service (normale Leistung)</SelectItem>
                                        <SelectItem value='BUNDLE'>Bundle (Paket mit mehreren Einheiten)</SelectItem>
                                    </SelectContent>
                                </Select>
                                <p className='text-xs text-muted-foreground'>
                                    {createType === 'SERVICE' 
                                        ? 'Services können mehrere Varianten und Add-Ons haben.'
                                        : 'Bundles repräsentieren ein Paket mit mehreren Einheiten einer Behandlung.'}
                                </p>
                            </div>

                            {/* Bundle Selection (only for BUNDLE type) */}
                            {createType === 'BUNDLE' && (
                                <div className='space-y-2 bg-amber-50 p-4 rounded-lg border border-amber-200'>
                                    <Label>Bundle auswählen *</Label>
                                    <div className='relative'>
                                        <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground' />
                                        <Input
                                            placeholder='Bundle suchen...'
                                            value={bundleSearch}
                                            onChange={(e) => setBundleSearch(e.target.value)}
                                            className='pl-9'
                                        />
                                    </div>
                                    {bundleSearch.length >= 1 && (
                                        <div className='max-h-48 overflow-y-auto border rounded bg-white'>
                                            {availableBundles
                                                .filter((b) => 
                                                    b.name.toLowerCase().includes(bundleSearch.toLowerCase()) ||
                                                    b.storeVariants.some(sv => sv.treatmentVariant.name.toLowerCase().includes(bundleSearch.toLowerCase()))
                                                )
                                                .map((bundle) => (
                                                    <div
                                                        key={bundle.id}
                                                        className='p-2 hover:bg-gray-100 cursor-pointer border-b last:border-b-0'
                                                        onClick={() => {
                                                            setCreateBundleId(bundle.id);
                                                            setBundleSearch('');
                                                        }}
                                                    >
                                                        <p className='font-medium'>{bundle.name || '-'}</p>
                                                        <p className='text-xs text-muted-foreground'>
                                                            {bundle.unitQuantity}x à {Number(bundle.unitPriceGross).toFixed(2)}€ = {(Number(bundle.unitPriceGross) * bundle.unitQuantity).toFixed(2)}€
                                                            {bundle.storeVariants.length > 0 && ` • ${bundle.storeVariants.length} Standort(e)`}
                                                        </p>
                                                    </div>
                                                ))}
                                            {availableBundles.filter((b) => 
                                                b.name.toLowerCase().includes(bundleSearch.toLowerCase()) ||
                                                b.storeVariants.some(sv => sv.treatmentVariant.name.toLowerCase().includes(bundleSearch.toLowerCase()))
                                            ).length === 0 && (
                                                <p className='text-sm text-muted-foreground py-4 text-center'>
                                                    Keine Bundles gefunden.
                                                </p>
                                            )}
                                        </div>
                                    )}
                                    {createBundleId && (
                                        <div className='flex items-center gap-2 text-sm text-green-600'>
                                            <Gift className='h-4 w-4' />
                                            Ausgewählt: {availableBundles.find((b) => b.id === createBundleId)?.name || '-'}
                                            <Button
                                                variant='ghost'
                                                size='sm'
                                                className='h-6 px-2'
                                                onClick={() => setCreateBundleId('')}
                                            >
                                                <X className='h-3 w-3' />
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className='grid grid-cols-2 gap-4'>
                                <div className='space-y-2'>
                                    <Label htmlFor='nameDe'>Name (Deutsch) *</Label>
                                    <Input
                                        id='nameDe'
                                        value={formData.nameDe}
                                        onChange={(e) => setFormData({ ...formData, nameDe: e.target.value })}
                                        placeholder='z.B. Hautanalyse'
                                    />
                                </div>
                                <div className='space-y-2'>
                                    <Label htmlFor='nameEn'>Name (Englisch) *</Label>
                                    <Input
                                        id='nameEn'
                                        value={formData.nameEn}
                                        onChange={(e) => setFormData({ ...formData, nameEn: e.target.value })}
                                        placeholder='e.g. Skin Analysis'
                                    />
                                </div>
                                <div className='space-y-2'>
                                    <Label htmlFor='descDe'>Beschreibung (Deutsch)</Label>
                                    <Textarea
                                        id='descDe'
                                        value={formData.descriptionDe}
                                        onChange={(e) => setFormData({ ...formData, descriptionDe: e.target.value })}
                                        rows={3}
                                    />
                                </div>
                                <div className='space-y-2'>
                                    <Label htmlFor='descEn'>Beschreibung (Englisch)</Label>
                                    <Textarea
                                        id='descEn'
                                        value={formData.descriptionEn}
                                        onChange={(e) => setFormData({ ...formData, descriptionEn: e.target.value })}
                                        rows={3}
                                    />
                                </div>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant='outline' onClick={() => {
                                setIsCreateDialogOpen(false);
                                setCreateType('SERVICE');
                                setCreateBundleId('');
                                setBundleSearch('');
                            }}>
                                Abbrechen
                            </Button>
                            <Button onClick={handleCreate} disabled={createMutation.isPending}>
                                {createMutation.isPending && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
                                Erstellen & Bearbeiten
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
                </div>
            </div>

            {/* Category Navigation */}
            <div className='space-y-2 mb-4'>
                <div className='flex flex-wrap gap-2'>
                    <Button
                        variant={selectedFilter === null ? 'default' : 'outline'}
                        size='sm'
                        onClick={() => setSelectedFilter(null)}
                    >
                        Alle ({allItems.length})
                    </Button>
                    {categories
                        .filter((cat) => !cat.parentId) // Only top-level categories
                        .map((category) => (
                            <Button
                                key={category.id}
                                variant={selectedFilter === `cat-${category.id}` ? 'default' : 'outline'}
                                size='sm'
                                onClick={() => setSelectedFilter(`cat-${category.id}`)}
                            >
                                {category.nameDe} ({categoryCounts[category.id] || 0})
                            </Button>
                        ))}
                    {unassignedCount > 0 && (
                        <Button
                            variant={selectedFilter === 'unassigned' ? 'default' : 'outline'}
                            size='sm'
                            onClick={() => setSelectedFilter('unassigned')}
                        >
                            Nicht zugeordnet ({unassignedCount})
                        </Button>
                    )}
                </div>
                {/* Symptom Category Navigation */}
                {symptomCategories.length > 0 && (
                    <div className='flex flex-wrap gap-2 items-center'>
                        <span className='text-xs text-muted-foreground font-medium'>Symptome:</span>
                        {symptomCategories
                            .filter((cat) => !cat.parentId) // Only top-level symptom categories
                            .map((symptomCategory) => (
                                <Button
                                    key={symptomCategory.id}
                                    variant={selectedFilter === `sym-${symptomCategory.id}` ? 'default' : 'outline'}
                                    size='sm'
                                    onClick={() => setSelectedFilter(`sym-${symptomCategory.id}`)}
                                >
                                    {symptomCategory.nameDe} ({symptomCategoryCounts[symptomCategory.id] || 0})
                                </Button>
                            ))}
                    </div>
                )}
                {/* Name Search */}
                <div className='relative max-w-sm'>
                    <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground' />
                    <Input
                        placeholder='Nach Name suchen...'
                        value={nameSearch}
                        onChange={(e) => setNameSearch(e.target.value)}
                        className='pl-9'
                    />
                    {nameSearch && (
                        <Button
                            variant='ghost'
                            size='icon'
                            className='absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6'
                            onClick={() => setNameSearch('')}
                        >
                            <X className='h-3 w-3' />
                        </Button>
                    )}
                </div>
            </div>

            {items.length === 0 ? (
                <Card>
                    <CardContent className='py-12 text-center text-muted-foreground'>
                        {selectedFilter === null
                            ? 'Noch keine buchbaren Leistungen erstellt. Klicken Sie auf "Neue Leistung" um zu beginnen.'
                            : selectedFilter === 'unassigned'
                                ? 'Keine Leistungen ohne Kategorie oder Symptom-Kategorie.'
                                : selectedFilter.startsWith('sym-')
                                    ? 'Keine Leistungen in dieser Symptom-Kategorie.'
                                    : 'Keine Leistungen in dieser Kategorie.'}
                    </CardContent>
                </Card>
            ) : (
                <div className='bg-white rounded-lg shadow-sm p-6'>
                    <div className='rounded-md border border-gray-200 overflow-hidden'>
                        <Table>
                            <TableHeader className='bg-gray-50'>
                                <TableRow>
                                    <TableHead className='font-medium text-gray-700'>Name</TableHead>
                                    <TableHead className='font-medium text-gray-700'>Typ</TableHead>
                                    <TableHead className='font-medium text-gray-700'>Kategorie</TableHead>
                                    <TableHead className='font-medium text-gray-700'>Symptom</TableHead>
                                    <TableHead className='font-medium text-gray-700'>Standorte</TableHead>
                                    <TableHead className='font-medium text-gray-700'>Sichtbarkeit</TableHead>
                                    <TableHead className='font-medium text-gray-700 w-[130px]'>Aktionen</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {items.map((item) => {
                                    const mismatchInfo = getItemStoreMismatchInfo(item);
                                    return (
                                    <TableRow key={item.id} className='hover:bg-gray-50 transition-colors'>
                                        <TableCell className='font-medium'>
                                            <div className='flex flex-col gap-1'>
                                                <div className='flex items-center gap-2'>
                                                    <span>{item.nameDe}</span>
                                                    {mismatchInfo.hasAnyMismatch && (
                                                        <TooltipProvider>
                                                            <Tooltip>
                                                                <TooltipTrigger>
                                                                    <AlertTriangle className='h-4 w-4 text-amber-500 shrink-0' />
                                                                </TooltipTrigger>
                                                                <TooltipContent>
                                                                    <p>Standort-Konfiguration könnte Probleme verursachen</p>
                                                                </TooltipContent>
                                                            </Tooltip>
                                                        </TooltipProvider>
                                                    )}
                                                </div>
                                                {item.tagAssociations?.length > 0 && (
                                                    <div className='flex flex-wrap gap-1'>
                                                        {item.tagAssociations.map((ta) => (
                                                            <TagPreview key={ta.tag.id} color={ta.tag.color} label={ta.tag.labelDe} />
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant={item.bundle ? 'default' : 'outline'} className={item.bundle ? 'bg-amber-100 text-amber-800 hover:bg-amber-100' : ''}>
                                                {item.bundle ? 'Bundle' : 'Leistung'}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            <div className='flex flex-wrap gap-1'>
                                                {item.categoryAssociations?.length === 0 ? (
                                                    <span className='text-muted-foreground text-xs'>-</span>
                                                ) : (
                                                    item.categoryAssociations?.map((ca) => (
                                                        <Badge key={ca.category.id} variant='outline' className='text-xs'>
                                                            {ca.category.nameDe}
                                                        </Badge>
                                                    ))
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className='flex flex-wrap gap-1'>
                                                {item.symptomCategoryAssociations?.length === 0 ? (
                                                    <span className='text-muted-foreground text-xs'>-</span>
                                                ) : (
                                                    item.symptomCategoryAssociations?.map((sca) => (
                                                        <Badge key={sca.symptomCategory.id} variant='outline' className='text-xs'>
                                                            {sca.symptomCategory.nameDe}
                                                        </Badge>
                                                    ))
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className='flex flex-wrap gap-1'>
                                                {item.isDigital ? (
                                                    <Badge variant='outline' className='text-xs'>Digital</Badge>
                                                ) : item.storeAssociations?.length === 0 ? (
                                                    <span className='text-muted-foreground text-xs'>Keine</span>
                                                ) : (
                                                    item.storeAssociations?.map((sa) => (
                                                        <Badge key={sa.store.id} variant='secondary' className='text-xs'>
                                                            {sa.store.name}
                                                        </Badge>
                                                    ))
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className='flex flex-col gap-1'>
                                                <Badge variant='outline' className={`text-xs ${item.hideFromStaff ? 'border-red-200 bg-red-50 text-red-700' : 'border-green-200 bg-green-50 text-green-700'}`}>
                                                    {item.hideFromStaff ? 'Mitarbeiter: Verborgen' : 'Mitarbeiter: Sichtbar'}
                                                </Badge>
                                                <Badge variant='outline' className={`text-xs ${item.hideFromPublic ? 'border-red-200 bg-red-50 text-red-700' : 'border-green-200 bg-green-50 text-green-700'}`}>
                                                    {item.hideFromPublic ? 'Kunden: Verborgen' : 'Kunden: Sichtbar'}
                                                </Badge>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className='flex gap-1'>
                                                <Button
                                                    variant='ghost'
                                                    size='icon'
                                                    onClick={() => setEditingItemId(item.id)}
                                                    title='Bearbeiten'
                                                >
                                                    <Edit className='h-4 w-4' />
                                                </Button>
                                                <Button
                                                    variant='ghost'
                                                    size='icon'
                                                    onClick={() => duplicateMutation.mutate({ id: item.id })}
                                                    disabled={duplicateMutation.isPending}
                                                    title='Kopieren'
                                                >
                                                    <Copy className='h-4 w-4' />
                                                </Button>
                                                <Button
                                                    variant='ghost'
                                                    size='icon'
                                                    onClick={() => handleDelete(item.id)}
                                                    disabled={deleteMutation.isPending}
                                                    title='Löschen'
                                                >
                                                    <Trash2 className='h-4 w-4 text-destructive' />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </div>
                </div>
            )}

            {/* Full Edit Dialog */}
            <Dialog open={editingItemId !== null} onOpenChange={(open) => !open && setEditingItemId(null)}>
                <DialogContent className='max-w-5xl max-h-[90vh] overflow-y-auto'>
                    <DialogHeader>
                        <DialogTitle className='flex items-center gap-2'>
                            Buchbare Leistung bearbeiten
                            {editingItem?.type === 'BUNDLE' && (
                                <Badge variant='secondary' className='bg-amber-100 text-amber-800'>
                                    <Gift className='h-3 w-3 mr-1' />
                                    Bundle
                                </Badge>
                            )}
                            {editingItem?.type === 'SERVICE' && (
                                <Badge variant='secondary'>
                                    Service
                                </Badge>
                            )}
                        </DialogTitle>
                        <DialogDescription>
                            Bearbeiten Sie alle Details dieser buchbaren Leistung.
                            {editingItem?.type === 'BUNDLE' && ' Der Typ kann nach der Erstellung nicht mehr geändert werden.'}
                        </DialogDescription>
                    </DialogHeader>

                    <Tabs defaultValue='basic' className='w-full'>
                        <TabsList className='mb-4'>
                            <TabsTrigger value='basic'>Grunddaten</TabsTrigger>
                            <TabsTrigger value='images'>Bilder ({editingItem?.images?.length || 0})</TabsTrigger>
                            {editingItem?.type === 'SERVICE' && (
                                <>
                                    <TabsTrigger value='variants' className='flex items-center gap-1'>
                                        Varianten ({editingItem?.variantOptions?.length || 0})
                                        {editingItemMismatchInfo?.hasVariantMismatch && (
                                            <AlertTriangle className='h-3 w-3 text-amber-500' />
                                        )}
                                    </TabsTrigger>
                                    <TabsTrigger value='addons' className='flex items-center gap-1'>
                                        Add-Ons ({editingItem?.addOns?.length || 0})
                                        {editingItemMismatchInfo?.hasAddOnMismatch && (
                                            <AlertTriangle className='h-3 w-3 text-amber-500' />
                                        )}
                                    </TabsTrigger>
                                </>
                            )}
                            {editingItem?.type === 'BUNDLE' && (
                                <TabsTrigger value='bundle' className='flex items-center gap-1'>
                                    <Gift className='h-3 w-3' />
                                    Bundle Details
                                </TabsTrigger>
                            )}
                            <TabsTrigger value='stores'>Standorte ({formData.isDigital ? 'Digital' : editingItemStoreIds.length})</TabsTrigger>
                            <TabsTrigger value='discountCodes'>Rabattcodes ({assignedDiscountCodeIds.length})</TabsTrigger>
                        </TabsList>

                        {/* Basic Info Tab */}
                        <TabsContent value='basic' className='space-y-6'>
                            <div className='grid grid-cols-2 gap-4'>
                                <div className='space-y-2'>
                                    <Label>Name (Deutsch)</Label>
                                    <Input
                                        value={formData.nameDe}
                                        onChange={(e) => setFormData({ ...formData, nameDe: e.target.value })}
                                    />
                                </div>
                                <div className='space-y-2'>
                                    <Label>Name (Englisch)</Label>
                                    <Input
                                        value={formData.nameEn}
                                        onChange={(e) => setFormData({ ...formData, nameEn: e.target.value })}
                                    />
                                </div>
                                <div className='space-y-2'>
                                    <Label>Beschreibung (Deutsch)</Label>
                                    <Textarea
                                        value={formData.descriptionDe}
                                        onChange={(e) => setFormData({ ...formData, descriptionDe: e.target.value })}
                                        rows={4}
                                    />
                                </div>
                                <div className='space-y-2'>
                                    <Label>Beschreibung (Englisch)</Label>
                                    <Textarea
                                        value={formData.descriptionEn}
                                        onChange={(e) => setFormData({ ...formData, descriptionEn: e.target.value })}
                                        rows={4}
                                    />
                                </div>
                            </div>
                            <div className='space-y-3'>
                                <div className='flex items-center gap-2'>
                                    <Switch
                                        checked={formData.hideFromPublic}
                                        onCheckedChange={(checked) => setFormData({ ...formData, hideFromPublic: checked })}
                                    />
                                    <Label>Von Kunden verbergen</Label>
                                </div>
                                <div className='flex items-center gap-2'>
                                    <Switch
                                        checked={formData.hideFromStaff}
                                        onCheckedChange={(checked) => setFormData({ ...formData, hideFromStaff: checked })}
                                    />
                                    <Label>Von Mitarbeitern verbergen</Label>
                                </div>
                                <div className='space-y-2'>
                                    <Label>Launch Date</Label>
                                    <p className='text-xs text-gray-500'>
                                        Wenn gesetzt, werden Termine erst ab diesem Datum im Buchungs-Funnel angezeigt.
                                    </p>
                                    <div className='flex gap-2'>
                                        <Input
                                            type='date'
                                            className='w-full'
                                            value={formData.launchDate ?? ''}
                                            onChange={(e) => setFormData({ ...formData, launchDate: e.target.value || null })}
                                        />
                                        {formData.launchDate && (
                                            <Button
                                                type='button'
                                                variant='outline'
                                                size='icon'
                                                onClick={() => setFormData({ ...formData, launchDate: null })}
                                            >
                                                <X className='h-4 w-4' />
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <Separator />

                            <div className='space-y-3'>
                                <div>
                                    <h4 className='font-medium text-lg'>Tags</h4>
                                    <p className='text-xs text-gray-500'>
                                        Werden im Buchungs-Funnel oben rechts auf der Karte angezeigt. Neue Tags legen Sie im Tab „Tags“ an.
                                    </p>
                                </div>
                                {tags.length === 0 ? (
                                    <p className='text-muted-foreground text-sm'>Noch keine Tags angelegt.</p>
                                ) : (
                                    <div className='flex flex-wrap gap-3'>
                                        {tags.map((tag) => (
                                            <label
                                                key={tag.id}
                                                className='flex items-center gap-2 rounded-md border border-gray-200 px-3 py-2 cursor-pointer hover:bg-gray-50'
                                            >
                                                <Checkbox
                                                    checked={localTagIds.includes(tag.id)}
                                                    onCheckedChange={() => handleToggleTag(tag.id)}
                                                />
                                                <TagPreview color={tag.color} label={tag.labelDe} />
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <Separator />

                            {/* "Mehr Dazu" content moved here */}
                            <h4 className='font-medium text-lg'>Mehr Dazu / Details</h4>
                            <div className='grid grid-cols-2 gap-4'>
                                <div className='space-y-4'>
                                    <h5 className='font-medium text-sm'>Vorteile (Deutsch)</h5>
                                    <div className='flex gap-2'>
                                        <Input
                                            value={newBenefitDe}
                                            onChange={(e) => setNewBenefitDe(e.target.value)}
                                            placeholder='Neuen Vorteil eingeben'
                                            onKeyDown={(e) => e.key === 'Enter' && handleAddBenefit('de')}
                                        />
                                        <Button onClick={() => handleAddBenefit('de')} size='icon'>
                                            <Plus className='h-4 w-4' />
                                        </Button>
                                    </div>
                                    <div className='flex flex-wrap gap-2'>
                                        {formData.keyBenefitsDe.map((benefit, idx) => (
                                            <Badge key={idx} variant='secondary' className='flex items-center gap-1'>
                                                {benefit}
                                                <div
                                                    className='w-3 h-3 cursor-pointer p-0 m-0'
                                                    onClick={() => handleRemoveBenefit('de', idx)}
                                                >
                                                    <X className='h-3 w-3' />
                                                </div>
                                            </Badge>
                                        ))}
                                    </div>
                                </div>
                                <div className='space-y-4'>
                                    <h5 className='font-medium text-sm'>Key Benefits (English)</h5>
                                    <div className='flex gap-2'>
                                        <Input
                                            value={newBenefitEn}
                                            onChange={(e) => setNewBenefitEn(e.target.value)}
                                            placeholder='Add new benefit'
                                            onKeyDown={(e) => e.key === 'Enter' && handleAddBenefit('en')}
                                        />
                                        <Button onClick={() => handleAddBenefit('en')} size='icon'>
                                            <Plus className='h-4 w-4' />
                                        </Button>
                                    </div>
                                    <div className='flex flex-wrap gap-2'>
                                        {formData.keyBenefitsEn.map((benefit, idx) => (
                                            <Badge key={idx} variant='secondary' className='flex items-center gap-1'>
                                                {benefit}
                                                <div
                                                    className='w-3 h-3 cursor-pointer p-0 m-0'
                                                    onClick={() => handleRemoveBenefit('en', idx)}
                                                >
                                                    <X className='h-3 w-3' />
                                                </div>
                                            </Badge>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className='grid grid-cols-2 gap-4'>
                                <div className='space-y-2'>
                                    <Label>Das solltest du wissen (Deutsch)</Label>
                                    <Textarea
                                        value={formData.youShouldKnowThisDe}
                                        onChange={(e) => setFormData({ ...formData, youShouldKnowThisDe: e.target.value })}
                                        rows={5}
                                        placeholder='Wichtige Hinweise für Kunden...'
                                    />
                                </div>
                                <div className='space-y-2'>
                                    <Label>You Should Know This (English)</Label>
                                    <Textarea
                                        value={formData.youShouldKnowThisEn}
                                        onChange={(e) => setFormData({ ...formData, youShouldKnowThisEn: e.target.value })}
                                        rows={5}
                                        placeholder='Important notes for customers...'
                                    />
                                </div>
                            </div>
                        </TabsContent>

                        {/* Images Tab */}
                        <TabsContent value='images' className='space-y-4'>
                            <div className='flex justify-between items-center'>
                                <p className='text-sm text-muted-foreground'>
                                    Bilder werden auf max. 500px skaliert.
                                </p>
                                <input
                                    ref={fileInputRef}
                                    type='file'
                                    accept='image/*'
                                    className='hidden'
                                    onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) handleImageUpload(file);
                                        e.target.value = '';
                                    }}
                                />
                                <Button onClick={() => fileInputRef.current?.click()} disabled={isUploading}>
                                    {isUploading ? (
                                        <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                                    ) : (
                                        <Upload className='mr-2 h-4 w-4' />
                                    )}
                                    Bild hochladen
                                </Button>
                            </div>

                            <div className='grid grid-cols-4 gap-4'>
                                {editingItem?.images?.map((image) => (
                                    <div key={image.id} className='relative group'>
                                        <img
                                            src={image.url}
                                            alt={image.altText || ''}
                                            className='w-full aspect-square object-cover rounded-lg border'
                                        />
                                        <Button
                                            variant='destructive'
                                            size='icon'
                                            className='absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity'
                                            onClick={() => deleteImageMutation.mutate({ imageId: image.id })}
                                        >
                                            <Trash2 className='h-4 w-4' />
                                        </Button>
                                    </div>
                                ))}
                                {(!editingItem?.images || editingItem.images.length === 0) && (
                                    <div className='col-span-4 py-8 text-center text-muted-foreground border-2 border-dashed rounded-lg'>
                                        <Image className='mx-auto h-12 w-12 mb-2 opacity-50' />
                                        Keine Bilder vorhanden
                                    </div>
                                )}
                            </div>
                        </TabsContent>

                        {/* Variants Tab (only for SERVICE type) */}
                        {editingItem?.type === 'SERVICE' && (
                        <TabsContent value='variants' className='space-y-4'>
                            {/* Warning banner for variant store mismatches */}
                            {editingItemMismatchInfo?.hasVariantMismatch && (
                                <div className='p-4 bg-amber-50 border border-amber-200 rounded-lg'>
                                    <div className='flex items-start gap-3'>
                                        <AlertTriangle className='h-5 w-5 text-amber-500 shrink-0 mt-0.5' />
                                        <div className='space-y-2'>
                                            <p className='font-medium text-amber-800'>Standort-Konfigurationsproblem</p>
                                            {editingItemMismatchInfo.variantIssues.map((issue, idx) => (
                                                <p key={idx} className='text-sm text-amber-700'>
                                                    Die Variante <strong>"{issue.name}"</strong> ist nur für{' '}
                                                    <strong>{issue.variantStores.join(', ')}</strong> konfiguriert,
                                                    aber diese Leistung ist für{' '}
                                                    <strong>{issue.itemStores.join(', ')}</strong> konfiguriert.
                                                    Da es keine Überschneidung gibt, wird diese Variante in keinem Standort buchbar sein.
                                                </p>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className='p-4 bg-muted/50 rounded-lg space-y-4'>
                                <h4 className='font-medium'>Neue Variante hinzufügen</h4>
                                <p className='text-sm text-muted-foreground'>
                                    Verknüpfen Sie Treatment-Varianten mit dieser buchbaren Leistung.
                                    Bei mehreren Varianten sieht der Kunde eine Auswahl.
                                </p>
                                <div className='flex gap-2'>
                                    <div className='flex-1 relative'>
                                        <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground' />
                                        <Input
                                            placeholder='Treatment-Variante suchen...'
                                            value={variantSearch}
                                            onChange={(e) => setVariantSearch(e.target.value)}
                                            className='pl-9'
                                        />
                                    </div>
                                </div>
                                {variantSearch.length >= 2 && (
                                    <div className='max-h-64 overflow-y-auto border rounded-lg bg-white'>
                                        {availableVariants
                                            .filter((v) => 
                                                !editingItem?.variantOptions?.some((vo) => vo.treatmentVariant.id === v.id) &&
                                                v.name.toLowerCase().includes(variantSearch.toLowerCase())
                                            )
                                            .map((variant) => {
                                                const variantStoreIds = variant.storeAssociations?.map((sa) => sa.store.id) || [];
                                                const hasNoStoreRestrictions = variantStoreIds.length === 0;
                                                const bookableItemHasNoStoreRestrictions = editingItemStoreIds.length === 0;
                                                
                                                // Check if variant is compatible with bookable item stores
                                                const isCompatible = hasNoStoreRestrictions || bookableItemHasNoStoreRestrictions || 
                                                    variantStoreIds.some((storeId) => editingItemStoreIds.includes(storeId));

                                                return (
                                                    <div
                                                        key={variant.id}
                                                        className={`flex items-center justify-between p-3 border-b last:border-b-0 ${
                                                            isCompatible 
                                                                ? 'hover:bg-muted cursor-pointer' 
                                                                : 'bg-muted/30 opacity-60 cursor-not-allowed'
                                                        }`}
                                                        onClick={() => isCompatible && handleAddVariantOption(variant.id)}
                                                    >
                                                        <div className='flex-1'>
                                                            <p className='font-medium'>{variant.name}</p>
                                                            <p className='text-xs text-muted-foreground'>
                                                                {variant.treatment?.category?.name} • {(variant.priceCents / 100).toFixed(2)} €
                                                            </p>
                                                            <div className='flex flex-wrap gap-1 mt-1'>
                                                                {hasNoStoreRestrictions ? (
                                                                    <Badge variant='outline' className='text-[10px] py-0'>
                                                                        Alle Standorte
                                                                    </Badge>
                                                                ) : (
                                                                    variant.storeAssociations?.map((sa) => (
                                                                        <Badge 
                                                                            key={sa.store.id} 
                                                                            variant='outline' 
                                                                            className='text-[10px] py-0'
                                                                        >
                                                                            {sa.store.name}
                                                                        </Badge>
                                                                    ))
                                                                )}
                                                            </div>
                                                            {!isCompatible && (
                                                                <p className='text-xs text-destructive mt-1'>
                                                                    Nicht verfügbar: Standorte stimmen nicht überein
                                                                </p>
                                                            )}
                                                        </div>
                                                        {isCompatible && <Plus className='h-4 w-4 text-muted-foreground' />}
                                                    </div>
                                                );
                                            })}
                                        {availableVariants.filter((v) => 
                                            !editingItem?.variantOptions?.some((vo) => vo.treatmentVariant.id === v.id) &&
                                            v.name.toLowerCase().includes(variantSearch.toLowerCase())
                                        ).length === 0 && (
                                            <p className='text-sm text-muted-foreground py-4 text-center'>
                                                Keine Varianten gefunden.
                                            </p>
                                        )}
                                    </div>
                                )}
                                {variantSearch.length === 1 && (
                                    <p className='text-sm text-muted-foreground'>
                                        Geben Sie mindestens 2 Zeichen ein, um zu suchen.
                                    </p>
                                )}
                            </div>

                            <Separator />

                            <div className='space-y-2'>
                                <h4 className='font-medium'>Verknüpfte Varianten</h4>
                                {editingItem?.variantOptions?.map((option) => {
                                    // Use store associations directly from the treatment variant
                                    const variantStoreAssociations = (option.treatmentVariant as { storeAssociations?: { store: { id: number; name: string } }[] }).storeAssociations || [];
                                    const variantStoreIds = variantStoreAssociations.map((sa) => sa.store.id);
                                    const hasNoStoreRestrictions = variantStoreIds.length === 0;
                                    const bookableItemHasNoStoreRestrictions = editingItemStoreIds.length === 0;
                                    
                                    // Check if there's a store mismatch warning
                                    const hasStoreMismatch = !hasNoStoreRestrictions && !bookableItemHasNoStoreRestrictions &&
                                        !variantStoreIds.some((storeId) => editingItemStoreIds.includes(storeId));

                                    return (
                                        <Card key={option.id} className={hasStoreMismatch ? 'border-amber-400' : ''}>
                                            <CardContent className='p-4'>
                                                <div className='flex items-start justify-between'>
                                                    <div className='flex-1'>
                                                        <div className='flex items-center gap-2'>
                                                            <span
                                                                className='font-medium cursor-pointer hover:underline flex items-center gap-1'
                                                                onClick={() => navigate(`/treatment/${option.treatmentVariant.treatmentId}/variants/${option.treatmentVariant.id}`)}
                                                            >
                                                                {option.treatmentVariant.name}
                                                                <ExternalLink className='h-3 w-3 text-muted-foreground' />
                                                            </span>
                                                            {hasStoreMismatch && (
                                                                <AlertTriangle className='h-4 w-4 text-amber-500' />
                                                            )}
                                                        </div>
                                                        <p className='text-sm text-muted-foreground'>
                                                            {(option.treatmentVariant.priceCents / 100).toFixed(2)} € •{' '}
                                                            {option.treatmentVariant.steps?.[0]?.minuteEnd || 0} Min
                                                        </p>
                                                        {/* Store badges */}
                                                        <div className='flex flex-wrap gap-1 mt-1'>
                                                            {hasNoStoreRestrictions ? (
                                                                <Badge variant='outline' className='text-[10px] py-0'>
                                                                    Alle Standorte
                                                                </Badge>
                                                            ) : variantStoreAssociations.map((sa) => (
                                                                <Badge 
                                                                    key={sa.store.id} 
                                                                    variant='outline' 
                                                                    className={`text-[10px] py-0 ${
                                                                        !bookableItemHasNoStoreRestrictions && !editingItemStoreIds.includes(sa.store.id)
                                                                            ? 'border-amber-400 text-amber-600'
                                                                            : ''
                                                                    }`}
                                                                >
                                                                    {sa.store.name}
                                                                </Badge>
                                                            ))}
                                                        </div>
                                                        {hasStoreMismatch && (
                                                            <p className='text-xs text-amber-600 mt-2 flex items-center gap-1'>
                                                                <AlertTriangle className='h-3 w-3' />
                                                                Warnung: Diese Variante ist nicht an den Standorten der buchbaren Leistung verfügbar.
                                                            </p>
                                                        )}
                                                        <div className='grid grid-cols-2 gap-2 mt-3'>
                                                            <div>
                                                                <Label className='text-xs'>Option-Label (DE)</Label>
                                                                <Input
                                                                    value={option.optionLabelDe}
                                                                    placeholder='z.B. 1 Zone'
                                                                    onChange={(e) =>
                                                                        updateVariantOptionMutation.mutate({
                                                                            id: option.id,
                                                                            optionLabelDe: e.target.value,
                                                                        })
                                                                    }
                                                                    className='h-8 text-sm'
                                                                />
                                                            </div>
                                                            <div>
                                                                <Label className='text-xs'>Option-Label (EN)</Label>
                                                                <Input
                                                                    value={option.optionLabelEn}
                                                                    placeholder='e.g. 1 Zone'
                                                                    onChange={(e) =>
                                                                        updateVariantOptionMutation.mutate({
                                                                            id: option.id,
                                                                            optionLabelEn: e.target.value,
                                                                        })
                                                                    }
                                                                    className='h-8 text-sm'
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <Button
                                                        variant='ghost'
                                                        size='icon'
                                                        onClick={() => removeVariantOptionMutation.mutate({ id: option.id })}
                                                    >
                                                        <Trash2 className='h-4 w-4 text-destructive' />
                                                    </Button>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    );
                                })}
                                {(!editingItem?.variantOptions || editingItem.variantOptions.length === 0) && (
                                    <p className='text-sm text-muted-foreground py-4 text-center'>
                                        Keine Varianten verknüpft. Suchen Sie oben nach Treatment-Varianten.
                                    </p>
                                )}
                            </div>
                        </TabsContent>
                        )}

                        {/* Add-Ons Tab (only for SERVICE type) */}
                        {editingItem?.type === 'SERVICE' && (
                        <TabsContent value='addons' className='space-y-4'>
                            {/* Warning banner for add-on store mismatches */}
                            {editingItemMismatchInfo?.hasAddOnMismatch && (
                                <div className='p-4 bg-amber-50 border border-amber-200 rounded-lg'>
                                    <div className='flex items-start gap-3'>
                                        <AlertTriangle className='h-5 w-5 text-amber-500 shrink-0 mt-0.5' />
                                        <div className='space-y-2'>
                                            <p className='font-medium text-amber-800'>Standort-Konfigurationsproblem</p>
                                            {editingItemMismatchInfo.addOnIssues.map((issue, idx) => (
                                                <p key={idx} className='text-sm text-amber-700'>
                                                    Das Add-On <strong>"{issue.name}"</strong> ist nur für{' '}
                                                    <strong>{issue.variantStores.join(', ')}</strong> konfiguriert,
                                                    aber diese Leistung ist für{' '}
                                                    <strong>{issue.itemStores.join(', ')}</strong> konfiguriert.
                                                    Da es keine Überschneidung gibt, wird dieses Add-On in keinem Standort buchbar sein.
                                                </p>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className='p-4 bg-muted/50 rounded-lg space-y-4'>
                                <h4 className='font-medium'>Neues Add-On hinzufügen</h4>
                                <p className='text-sm text-muted-foreground'>
                                    Add-Ons werden dem Kunden nach Auswahl dieser Leistung angeboten.
                                </p>
                                <div className='grid grid-cols-2 gap-4'>
                                    <div className='space-y-2'>
                                        <Label>Name (Deutsch) *</Label>
                                        <Input
                                            value={newAddOn.nameDe}
                                            onChange={(e) => setNewAddOn({ ...newAddOn, nameDe: e.target.value })}
                                            placeholder='z.B. Zusätzliche Behandlungszone'
                                        />
                                    </div>
                                    <div className='space-y-2'>
                                        <Label>Name (Englisch) *</Label>
                                        <Input
                                            value={newAddOn.nameEn}
                                            onChange={(e) => setNewAddOn({ ...newAddOn, nameEn: e.target.value })}
                                            placeholder='e.g. Additional Treatment Zone'
                                        />
                                    </div>
                                    <div className='space-y-2'>
                                        <Label>Beschreibung (Deutsch)</Label>
                                        <Textarea
                                            value={newAddOn.descriptionDe}
                                            onChange={(e) => setNewAddOn({ ...newAddOn, descriptionDe: e.target.value })}
                                            rows={2}
                                        />
                                    </div>
                                    <div className='space-y-2'>
                                        <Label>Beschreibung (Englisch)</Label>
                                        <Textarea
                                            value={newAddOn.descriptionEn}
                                            onChange={(e) => setNewAddOn({ ...newAddOn, descriptionEn: e.target.value })}
                                            rows={2}
                                        />
                                    </div>
                                    <div className='col-span-2 space-y-2'>
                                        <Label>Treatment-Variante für Preis *</Label>
                                        <div className='relative'>
                                            <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground' />
                                            <Input
                                                placeholder='Suchen...'
                                                value={addOnVariantSearch}
                                                onChange={(e) => setAddOnVariantSearch(e.target.value)}
                                                className='pl-9'
                                            />
                                        </div>
                                        {addOnVariantSearch.length >= 2 && (
                                            <div className='max-h-48 overflow-y-auto border rounded bg-white'>
                                                {availableVariants
                                                    .filter((v) => v.name.trim().toLowerCase().includes(addOnVariantSearch.trim().toLowerCase()))
                                                    .map((v) => {
                                                        const variantStoreIds = v.storeAssociations?.map((sa) => sa.store.id) || [];
                                                        const hasNoStoreRestrictions = variantStoreIds.length === 0;
                                                        const bookableItemHasNoStoreRestrictions = editingItemStoreIds.length === 0;
                                                        const isCompatible = hasNoStoreRestrictions || bookableItemHasNoStoreRestrictions || 
                                                            variantStoreIds.some((storeId) => editingItemStoreIds.includes(storeId));

                                                        return (
                                                            <div
                                                                key={v.id}
                                                                className={`p-3 border-b last:border-b-0 ${
                                                                    newAddOn.variantId === v.id 
                                                                        ? 'bg-primary/10' 
                                                                        : isCompatible 
                                                                            ? 'hover:bg-muted cursor-pointer' 
                                                                            : 'bg-muted/30 opacity-60 cursor-not-allowed'
                                                                }`}
                                                                onClick={() => {
                                                                    if (isCompatible) {
                                                                        setNewAddOn({ ...newAddOn, variantId: v.id });
                                                                        setAddOnVariantSearch('');
                                                                    }
                                                                }}
                                                            >
                                                                <div className='flex justify-between items-start'>
                                                                    <div>
                                                                        <p className='font-medium'>{v.name}</p>
                                                                        <p className='text-xs text-muted-foreground'>
                                                                            {(v.priceCents / 100).toFixed(2)} €
                                                                        </p>
                                                                        <div className='flex flex-wrap gap-1 mt-1'>
                                                                            {hasNoStoreRestrictions ? (
                                                                                <Badge variant='outline' className='text-[10px] py-0'>
                                                                                    Alle Standorte
                                                                                </Badge>
                                                                            ) : v.storeAssociations?.map((sa) => (
                                                                                <Badge 
                                                                                    key={sa.store.id} 
                                                                                    variant='outline' 
                                                                                    className='text-[10px] py-0'
                                                                                >
                                                                                    {sa.store.name}
                                                                                </Badge>
                                                                            ))}
                                                                        </div>
                                                                        {!isCompatible && (
                                                                            <p className='text-xs text-destructive mt-1'>
                                                                                Nicht verfügbar: Standorte stimmen nicht überein
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                {availableVariants.filter((v) => v.name.toLowerCase().includes(addOnVariantSearch.toLowerCase())).length === 0 && (
                                                    <p className='text-sm text-muted-foreground py-4 text-center'>
                                                        Keine Varianten gefunden.
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                        {addOnVariantSearch.length === 1 && (
                                            <p className='text-sm text-muted-foreground'>
                                                Geben Sie mindestens 2 Zeichen ein, um zu suchen.
                                            </p>
                                        )}
                                        {newAddOn.variantId && (
                                            <p className='text-sm text-green-600'>
                                                ✓ Variante ausgewählt:{' '}
                                                {availableVariants.find((v) => v.id === newAddOn.variantId)?.name ||
                                                    'Laden...'}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <Button onClick={handleAddAddOn} disabled={addAddOnMutation.isPending}>
                                    {addAddOnMutation.isPending && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
                                    Add-On hinzufügen
                                </Button>
                            </div>

                            <Separator />

                            <div className='space-y-2'>
                                <h4 className='font-medium'>Vorhandene Add-Ons</h4>
                                {editingItem?.addOns?.map((addOn) => {
                                    // Use store associations directly from the treatment variant
                                    const variantStoreAssociations = (addOn.treatmentVariant as { storeAssociations?: { store: { id: number; name: string } }[] }).storeAssociations || [];
                                    const variantStoreIds = variantStoreAssociations.map((sa) => sa.store.id);
                                    const hasNoStoreRestrictions = variantStoreIds.length === 0;
                                    const bookableItemHasNoStoreRestrictions = editingItemStoreIds.length === 0;
                                    
                                    // Check if there's a store mismatch warning
                                    const hasStoreMismatch = !hasNoStoreRestrictions && !bookableItemHasNoStoreRestrictions &&
                                        !variantStoreIds.some((storeId) => editingItemStoreIds.includes(storeId));

                                    return (
                                        <Card key={addOn.id} className={hasStoreMismatch ? 'border-amber-400' : ''}>
                                            <CardContent className='p-4'>
                                                <div className='flex items-start justify-between'>
                                                    <div>
                                                        <div className='flex items-center gap-2'>
                                                            <p className='font-medium'>
                                                                {addOn.nameDe} / {addOn.nameEn}
                                                            </p>
                                                            {hasStoreMismatch && (
                                                                <AlertTriangle className='h-4 w-4 text-amber-500' />
                                                            )}
                                                        </div>
                                                        <p className='text-sm text-muted-foreground'>
                                                            <span
                                                                className='cursor-pointer hover:underline inline-flex items-center gap-1'
                                                                onClick={() => navigate(`/treatment/${addOn.treatmentVariant.treatmentId}/variants/${addOn.treatmentVariant.id}`)}
                                                            >
                                                                {addOn.treatmentVariant.name}
                                                                <ExternalLink className='h-3 w-3' />
                                                            </span>
                                                            {' '} • {(addOn.treatmentVariant.priceCents / 100).toFixed(2)} €
                                                        </p>
                                                        {/* Store badges */}
                                                        <div className='flex flex-wrap gap-1 mt-1'>
                                                            {hasNoStoreRestrictions ? (
                                                                <Badge variant='outline' className='text-[10px] py-0'>
                                                                    Alle Standorte
                                                                </Badge>
                                                            ) : variantStoreAssociations.map((sa) => (
                                                                <Badge 
                                                                    key={sa.store.id} 
                                                                    variant='outline' 
                                                                    className={`text-[10px] py-0 ${
                                                                        !bookableItemHasNoStoreRestrictions && !editingItemStoreIds.includes(sa.store.id)
                                                                            ? 'border-amber-400 text-amber-600'
                                                                            : ''
                                                                    }`}
                                                                >
                                                                    {sa.store.name}
                                                                </Badge>
                                                            ))}
                                                        </div>
                                                        {hasStoreMismatch && (
                                                            <p className='text-xs text-amber-600 mt-2 flex items-center gap-1'>
                                                                <AlertTriangle className='h-3 w-3' />
                                                                Warnung: Diese Variante ist nicht an den Standorten der buchbaren Leistung verfügbar.
                                                            </p>
                                                        )}
                                                    </div>
                                                    <Button
                                                        variant='ghost'
                                                        size='icon'
                                                        onClick={() => removeAddOnMutation.mutate({ id: addOn.id })}
                                                    >
                                                        <Trash2 className='h-4 w-4 text-destructive' />
                                                    </Button>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    );
                                })}
                                {(!editingItem?.addOns || editingItem.addOns.length === 0) && (
                                    <p className='text-sm text-muted-foreground py-4 text-center'>Keine Add-Ons vorhanden.</p>
                                )}
                            </div>
                        </TabsContent>
                        )}

                        {/* Bundle Details Tab (only for BUNDLE type) */}
                        {editingItem?.type === 'BUNDLE' && (
                            <TabsContent value='bundle' className='space-y-4'>
                                <div className='p-4 bg-amber-50 rounded-lg border border-amber-200'>
                                    <h4 className='font-medium mb-4 flex items-center gap-2'>
                                        <Gift className='h-5 w-5 text-amber-600' />
                                        Verknüpftes Bundle
                                    </h4>
                                    {editingItem.bundle ? (
                                        <div className='space-y-3'>
                                            <div className='grid grid-cols-2 gap-4'>
                                                <div>
                                                    <p className='text-sm text-muted-foreground'>Bundle Name</p>
                                                    <span
                                                        className='font-medium cursor-pointer hover:underline flex items-center gap-1'
                                                        onClick={() => navigate('/cms/bundles')}
                                                    >
                                                        {editingItem.bundle.name || '-'}
                                                        <ExternalLink className='h-3 w-3 text-muted-foreground' />
                                                    </span>
                                                </div>
                                                <div>
                                                    <p className='text-sm text-muted-foreground'>Konfigurierte Standorte</p>
                                                    <p className='font-medium'>{editingItem.bundle.storeVariants.length} von {stores.length}</p>
                                                </div>
                                                <div>
                                                    <p className='text-sm text-muted-foreground'>Stückpreis</p>
                                                    <p className='font-medium'>
                                                        {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(Number(editingItem.bundle.unitPriceGross))}
                                                    </p>
                                                </div>
                                                <div>
                                                    <p className='text-sm text-muted-foreground'>Anzahl Einheiten</p>
                                                    <p className='font-medium'>{editingItem.bundle.unitQuantity}x</p>
                                                </div>
                                            </div>
                                            <Separator />
                                            <div>
                                                <p className='text-sm text-muted-foreground'>Gesamtpreis</p>
                                                <p className='text-xl font-bold text-amber-700'>
                                                    {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(
                                                        Number(editingItem.bundle.unitPriceGross) * editingItem.bundle.unitQuantity
                                                    )}
                                                </p>
                                            </div>
                                            {/* Store variant availability warning */}
                                            {(() => {
                                                // Determine which stores the bookable item is available at
                                                const itemStoreIds = editingItem.storeAssociations.length > 0
                                                    ? editingItem.storeAssociations.map(sa => sa.store.id)
                                                    : stores.map(s => s.id); // If no restrictions, available at all stores
                                                
                                                // Find stores where the bundle doesn't have a variant configured
                                                const bundleStoreIds = editingItem.bundle?.storeVariants.map(sv => sv.store.id) || [];
                                                const unconfiguredStores = stores.filter(
                                                    s => itemStoreIds.includes(s.id) && !bundleStoreIds.includes(s.id)
                                                );

                                                if (unconfiguredStores.length > 0) {
                                                    return (
                                                        <div className='mt-4 p-3 bg-red-50 border border-red-200 rounded-lg'>
                                                            <div className='flex items-start gap-2'>
                                                                <AlertTriangle className='h-5 w-5 text-red-600 shrink-0 mt-0.5' />
                                                                <div>
                                                                    <p className='font-medium text-red-800'>
                                                                        Bundle nicht an allen Standorten verfügbar
                                                                    </p>
                                                                    <p className='text-sm text-red-700 mt-1'>
                                                                        Dieses Bundle wird an folgenden Standorten nicht angezeigt, da keine Treatment-Variante konfiguriert ist:
                                                                    </p>
                                                                    <ul className='mt-2 text-sm text-red-700'>
                                                                        {unconfiguredStores.map(store => (
                                                                            <li key={store.id} className='flex items-center gap-1'>
                                                                                <MapPin className='h-3 w-3' />
                                                                                {store.name} ({store.city})
                                                                            </li>
                                                                        ))}
                                                                    </ul>
                                                                    <p className='text-sm text-red-600 mt-2'>
                                                                        Konfigurieren Sie die Varianten im Bundles-Bereich oder schränken Sie die Standorte dieser Leistung ein.
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                }

                                                return null;
                                            })()}
                                            {/* Show configured variants per store */}
                                            {editingItem.bundle.storeVariants.length > 0 && (
                                                <div className='mt-4'>
                                                    <p className='text-sm text-muted-foreground mb-2'>Konfigurierte Varianten pro Standort:</p>
                                                    <div className='space-y-2'>
                                                        {editingItem.bundle.storeVariants.map(sv => (
                                                            <div key={sv.store.id} className='flex items-center gap-2 text-sm bg-white p-2 rounded border'>
                                                                <MapPin className='h-4 w-4 text-muted-foreground' />
                                                                <span className='font-medium'>{sv.store.name}:</span>
                                                                <span
                                                                    className='cursor-pointer hover:underline inline-flex items-center gap-1'
                                                                    onClick={() => navigate(`/treatment/${sv.treatmentVariant.treatmentId}/variants/${sv.treatmentVariant.id}`)}
                                                                >
                                                                    {sv.treatmentVariant.name}
                                                                    <ExternalLink className='h-3 w-3' />
                                                                </span>
                                                                <span className='text-muted-foreground ml-auto'>
                                                                    {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(sv.treatmentVariant.priceCents / 100)}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <p className='text-muted-foreground'>Kein Bundle verknüpft.</p>
                                    )}
                                </div>
                                <p className='text-sm text-muted-foreground'>
                                    Das Bundle kann nach der Erstellung nicht mehr geändert werden. Um ein anderes Bundle zu verwenden, 
                                    erstellen Sie bitte eine neue buchbare Leistung. Varianten pro Standort können im Bundles-Bereich konfiguriert werden.
                                </p>
                            </TabsContent>
                        )}

                        {/* Stores Tab */}
                        <TabsContent value='stores' className='space-y-4'>
                            <div className='p-4 bg-muted/50 rounded-lg'>
                                <h4 className='font-medium mb-2'>Standort-Zuordnung</h4>
                                <p className='text-sm text-muted-foreground mb-4'>
                                    Wählen Sie die Standorte aus, an denen diese Leistung verfügbar ist.
                                    Nur ausgewählte Standorte werden berücksichtigt.
                                </p>

                                <div className='flex items-center gap-2 mb-4 p-3 border rounded-lg bg-white'>
                                    <Checkbox
                                        checked={formData.isDigital}
                                        onCheckedChange={(checked) => {
                                            const isDigital = checked === true;
                                            setFormData({ ...formData, isDigital });
                                            if (isDigital) {
                                                setLocalStoreIds([]);
                                            }
                                        }}
                                    />
                                    <div>
                                        <Label className='cursor-pointer'>Nur Digital</Label>
                                        <p className='text-xs text-muted-foreground'>
                                            Diese Leistung benötigt keinen physischen Standort.
                                        </p>
                                    </div>
                                </div>

                                <div className={`grid grid-cols-2 gap-2 max-h-64 overflow-y-auto border rounded-lg p-2 bg-white ${formData.isDigital ? 'opacity-50 pointer-events-none' : ''}`}>
                                    {stores.map((store) => {
                                        const isSelected = localStoreIds.includes(store.id);
                                        return (
                                            <div
                                                key={store.id}
                                                className={`flex items-center gap-2 p-3 rounded cursor-pointer hover:bg-muted transition-colors ${
                                                    isSelected ? 'bg-primary/10 border border-primary/30' : 'border border-transparent'
                                                }`}
                                                onClick={() => {
                                                    const newStoreIds = isSelected
                                                        ? localStoreIds.filter((id) => id !== store.id)
                                                        : [...localStoreIds, store.id];
                                                    setLocalStoreIds(newStoreIds);
                                                }}
                                            >
                                                <Checkbox checked={isSelected} />
                                                <div className='flex items-center gap-2'>
                                                    <MapPin className='h-4 w-4 text-muted-foreground' />
                                                    <div>
                                                        <span className='text-sm font-medium'>{store.name}</span>
                                                        <span className='text-xs text-muted-foreground ml-2'>{store.city}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {stores.length === 0 && (
                                        <p className='col-span-2 text-center text-sm text-muted-foreground py-4'>
                                            Keine Standorte verfügbar.
                                        </p>
                                    )}
                                </div>
                                {!formData.isDigital && localStoreIds.length === 0 && (
                                    <p className='text-sm text-amber-600 mt-2 flex items-center gap-1'>
                                        <span>⚠</span> Kein Standort ausgewählt — diese Leistung wird nirgends angezeigt.
                                    </p>
                                )}
                                {/* Show hint if there are unsaved changes */}
                                {JSON.stringify(localStoreIds.sort()) !== JSON.stringify(editingItemStoreIds.sort()) && (
                                    <p className='text-sm text-amber-600 mt-2 flex items-center gap-1'>
                                        <span>⚠</span> Ungespeicherte Änderungen
                                    </p>
                                )}
                            </div>
                        </TabsContent>

                        {/* Discount Codes Tab */}
                        <TabsContent value='discountCodes' className='space-y-4'>
                            <div className='p-4 bg-muted/50 rounded-lg'>
                                <h4 className='font-medium mb-2'>Rabattcode-Zuordnung</h4>
                                <p className='text-sm text-muted-foreground mb-4'>
                                    Wählen Sie die Rabattcodes aus, die für diese Leistung gelten sollen.
                                    Es werden nur Rabattcodes mit der Anwendungsebene &quot;Pro Einzelposition&quot; angezeigt —
                                    Rabattcodes auf den Gesamtbetrag gelten automatisch für alle Leistungen.
                                </p>

                                <DiscountCodeAssignmentList
                                    key={editingItemId}
                                    discountCodes={discountCodesData?.data}
                                    selectedIds={localDiscountCodeIds}
                                    onChange={setLocalDiscountCodeIds}
                                    hasChanges={hasDiscountCodeChanges}
                                />
                            </div>
                        </TabsContent>

                    </Tabs>

                    <DialogFooter className='mt-6'>
                        <Button variant='outline' onClick={() => setEditingItemId(null)}>
                            Schließen
                        </Button>
                        <Button onClick={handleSave} disabled={updateMutation.isPending || setStoresMutation.isPending || setDiscountCodesMutation.isPending}>
                            {(updateMutation.isPending || setStoresMutation.isPending || setDiscountCodesMutation.isPending) && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
                            Änderungen speichern
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default BookableItemsEditor;
